// subscription.tsx
import { Button, Flex, Typography, Space, Progress } from 'antd';
import {
  CrownOutlined,
  DesktopOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import React, { FunctionComponent } from 'react';
import { useStore } from '../../helpers/use-store';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface Props {
  displayCredits: boolean;
  displaySeries: boolean;
  displayCTA: boolean;
}

const Subscription: FunctionComponent<Props> = ({
  displayCredits,
  displaySeries,
  displayCTA,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    dataStore: { userStore, seriesStore, planStore },
  } = useStore();

  const isSubscribed = userStore.isSubscriptionActive;
  const plan = planStore.items.find(
    (p) => p._id === userStore.userSubscription.planId
  );
  const planName = plan?.name || 'Free';
  const planCredits = plan?.credits || 125; // Default to starter plan credits

  const credits = Math.floor(userStore.userSubscription.credits);
  const topupCredits = Math.floor(userStore.userSubscription.topupCredits || 0);
  const totalCredits = credits + topupCredits;
  const creditPercentage = (credits / planCredits) * 100;

  // Determine status based on percentage
  const getCreditStatus = () => {
    if (creditPercentage <= 25) return { color: '#ff4d4f', status: 'critical' };
    if (creditPercentage < 50) return { color: '#faad14', status: 'low' };
    return { color: '#52c41a', status: 'healthy' };
  };

  const creditStatus = getCreditStatus();
  const showPurchaseButton = creditStatus.status === 'critical' && isSubscribed;
  const showUpgradeButton = !isSubscribed || creditStatus.status === 'low';

  return (
    <div className="subscription-info">
      {/* Plan Header */}
      <div
        className={`subscription-plan-header ${
          !isSubscribed ? 'free-plan' : ''
        }`}
      >
        <Flex align="center" justify="space-between" style={{ width: '100%' }}>
          <Space size={8} style={{ minWidth: 0, overflow: 'hidden' }}>
            <div className="plan-icon-wrapper" style={{ flexShrink: 0 }}>
              <CrownOutlined />
            </div>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <Flex align="center" gap={6}>
                <Text className="plan-label" style={{ whiteSpace: 'nowrap' }}>
                  {t('subscription.plan')}
                </Text>
                {!isSubscribed && (
                  <Text
                    className="free-badge"
                    style={{ whiteSpace: 'nowrap', fontSize: '10px' }}
                  >
                    {t('subscription.freePlan')}
                  </Text>
                )}
              </Flex>
              <Text
                strong
                className="plan-name"
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}
              >
                {planName}
              </Text>
            </div>
          </Space>
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => navigate('/credits')}
            className="settings-button"
          />
        </Flex>
      </div>

      {/* Stats */}
      {displayCredits && (
        <div className="subscription-info-item">
          <Flex
            align="center"
            justify="space-between"
            style={{ width: '100%', marginBottom: '4px' }}
          >
            <Space size={8} style={{ flexShrink: 0 }}>
              <CrownOutlined style={{ color: creditStatus.color }} />
              <Text style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                {t('subscription.credits')}
              </Text>
            </Space>
            <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
              <Text
                strong
                style={{
                  fontSize: '13px',
                  color: creditStatus.color,
                  whiteSpace: 'nowrap',
                }}
              >
                {credits.toLocaleString()}
              </Text>
              {topupCredits > 0 && (
                <>
                  <Text
                    style={{
                      fontSize: '13px',
                      color: '#8c8c8c',
                    }}
                  >
                    +
                  </Text>
                  <Text
                    strong
                    style={{
                      fontSize: '13px',
                      color: '#eb2f96',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {topupCredits.toLocaleString()}
                  </Text>
                </>
              )}
            </Flex>
          </Flex>
          <Progress
            percent={creditPercentage}
            strokeColor={creditStatus.color}
            showInfo={false}
            size="small"
            style={{ marginTop: '2px' }}
          />
        </div>
      )}

      {displaySeries && (
        <div className="subscription-info-item">
          <Flex
            align="center"
            justify="space-between"
            style={{ width: '100%' }}
          >
            <Space size={8} style={{ flexShrink: 0 }}>
              <DesktopOutlined />
              <Text style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                {t('subscription.seriesAllowed')}
              </Text>
            </Space>
            <Text strong style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
              {seriesStore.items.length}/
              {userStore.userSubscription.allowedSeriesAmount}
            </Text>
          </Flex>
        </div>
      )}

      {/* CTA */}
      {displayCTA && (
        <div className="subscription-info-item" style={{ paddingTop: '8px' }}>
          {!isSubscribed ? (
            // Free users - always show upgrade
            <Button
              type="primary"
              block
              icon={<RocketOutlined />}
              onClick={() => navigate('/credits')}
              style={{ fontWeight: 500 }}
            >
              {t('subscription.upgrade')}
            </Button>
          ) : showPurchaseButton ? (
            <Button
              type="primary"
              danger
              block
              icon={<ThunderboltOutlined />}
              onClick={() => navigate('/credits')}
              style={{ fontWeight: 500 }}
            >
              {t('subscription.upgrade')}
            </Button>
          ) : showUpgradeButton ? (
            <Button
              type="primary"
              block
              onClick={() => navigate('/credits')}
              style={{ fontWeight: 500 }}
            >
              {t('subscription.upgrade')}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default observer(Subscription);
